import { Maatregel, OmleidingMaatregel, RijdtMaatregel, RijdtNietMaatregel, StopMaatregel, Vsm } from "@/entities/vsm";
import { TreinBadge } from "@/features/vsm/components/treinbadge.tsx";
import { determineMaxItems } from "@/features/vsm/utils.tsx";
import { Dot, Separated } from "@/shared/components";
import { getListPreview } from "@/shared/utils/list-preview.ts";

interface VsmSummaryProps {
    vsm: Vsm;
}

export function VsmSummary({ vsm }: Readonly<VsmSummaryProps>) {
    const maatregelen = vsm.maatregelen;
    const rijdtNiet = maatregelen.filter(Maatregel.filterBy("RijdtNiet"));
    const aanvullend = maatregelen.filter(Maatregel.filterBy("Aanvullend"));
    // exclude RijdtGroepMaatregel here, as it only complicates things otherwise
    const blijftRijden = maatregelen.filter(Maatregel.filterBy("Rijdt", (m) => m.type === "RijdtGroep")) as RijdtMaatregel[];

    return (
        <div className="grid gap-y-3 pr-3 pl-4" data-testid="vsm-summary">
            <Separated>
                {rijdtNiet.length > 0 && <TreinSerieRow type="RijdtNiet" maatregelen={rijdtNiet} />}
                {blijftRijden.length > 0 && <TreinSerieRow type="Rijdt" maatregelen={blijftRijden} />}
                {aanvullend.length > 0 && <Aanvullend maatregelen={aanvullend} />}
            </Separated>
        </div>
    );
}

interface TreinSerieRowProps {
    type: "Rijdt" | "RijdtNiet";
    maatregelen: (RijdtMaatregel | RijdtNietMaatregel)[];
}

function TreinSerieRow({ type, maatregelen }: TreinSerieRowProps) {
    const rowType = type == "Rijdt" ? "blijft-rijden" : "rijdt-niet";
    const maxItems = determineMaxItems(maatregelen);
    const { previewItems, remainder } = getListPreview(maatregelen, maxItems);

    return (
        <div className="grid grid-cols-[80px_4px_1fr] items-center gap-x-4">
            <h4 className="text-content-emphasis font-medium">{type == "Rijdt" ? "Blijft rijden" : "Rijdt niet"}</h4>
            <Dot size="s" />
            <ol className="flex flex-row gap-2" data-testid={rowType}>
                {previewItems.map((m) => (
                    <TreinBadge.VanNaar
                        key={m.id}
                        treinSerieOfNummer={m.treinserie}
                        van={m.vanStationCode}
                        naar={m.naarStationCode}
                        richting={m.richting}
                    />
                ))}
                {remainder > 0 && (
                    <p data-testid={`${rowType}-remainder`} data-remainder={remainder} className="text-s text-content-subtle font-normal">
                        +{remainder} series
                    </p>
                )}
            </ol>
        </div>
    );
}

interface AanvullendProps {
    maatregelen: (StopMaatregel | OmleidingMaatregel)[];
}

function Aanvullend({ maatregelen }: Readonly<AanvullendProps>) {
    const { previewItems, remainder } = getListPreview(maatregelen, 3);
    return (
        <div className="grid grid-cols-[80px_4px_1fr] items-center gap-x-4 gap-y-3">
            <h4 className="text-content-emphasis font-medium">Aanvullend</h4>
            <Dot size="s" />
            <ol className="flex flex-row gap-2" data-testid="aanvullend">
                {previewItems.map((m) => (
                    <TreinBadge.Aanvullend
                        key={m.id}
                        treinSerieOfNummer={m.treinserie}
                        van={m.vanStationCode}
                        naar={m.naarStationCode}
                        type={m.type}
                    />
                ))}
                {remainder > 0 && (
                    <p data-testid="aanvullend-remainder" data-remainder={remainder} className="text-s text-content-subtle font-normal">
                        +{remainder} series
                    </p>
                )}
            </ol>
        </div>
    );
}
